import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { RiCheckFill, RiSubtractLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';

/* Label next to a checkbox / radio: 500 13.5/auto white, gap 10 (382:6697, 351:6871). */
const choiceLabel = 'text-[0.84375rem] leading-auto font-medium text-fg';
const choiceDescription = 'text-11 text-fg-secondary';

export interface CheckboxProps {
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * Checkbox — kit 67:11978 (spec §4.12): 20, r4. Off: Primary BG + 1px White/10. On: `#3576ec` + check-fill 16 white.
 * Indeterminate: subtract-line 16. Disabled: White/5 + 1px White/10.
 */
export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  readOnly,
  required,
  name,
  value,
  label,
  description,
  id,
  'aria-label': ariaLabel,
  className,
}: CheckboxProps) {
  const indeterminate = checked === 'indeterminate';
  const box = (
    <BaseCheckbox.Root
      id={id}
      checked={checked === undefined ? undefined : checked === true}
      defaultChecked={defaultChecked}
      indeterminate={indeterminate}
      onCheckedChange={(next) => onCheckedChange?.(next)}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      name={name}
      value={value}
      aria-label={label ? undefined : ariaLabel}
      className={cn(
        'flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-4 border border-line bg-panel text-white',
        'transition-colors duration-150 hover:border-line-strong',
        'data-checked:border-transparent data-checked:bg-control data-indeterminate:border-transparent data-indeterminate:bg-control',
        'data-disabled:cursor-not-allowed data-disabled:border-line data-disabled:bg-subtle data-disabled:text-fg-tertiary',
        !label && className,
      )}
    >
      <BaseCheckbox.Indicator className="flex data-unchecked:hidden">
        {indeterminate ? <RiSubtractLine aria-hidden className="size-16" /> : <RiCheckFill aria-hidden className="size-16" />}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
  if (!label) return box;
  // Enclosing <label> — the pattern Base UI recommends for its <span> checkbox.
  return (
    <label
      className={cn('flex items-start gap-10', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer', className)}
    >
      {box}
      <span className="flex min-w-0 flex-col gap-2 pt-1">
        <span className={choiceLabel}>{label}</span>
        {description && <span className={choiceDescription}>{description}</span>}
      </span>
    </label>
  );
}

export interface RadioOption<T> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T> {
  options: readonly RadioOption<T>[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  orientation?: 'vertical' | 'horizontal';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

/** Radio dot — kit 67:11987: 20 round; off Primary BG + 1px White/10; on `#3576ec` + 8 px white dot. */
export function Radio<T>({ value, disabled, id, 'aria-label': ariaLabel }: { value: T; disabled?: boolean; id?: string; 'aria-label'?: string }) {
  return (
    <BaseRadio.Root
      id={id}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-full border border-line bg-panel',
        'transition-colors duration-150 hover:border-line-strong',
        'data-checked:border-transparent data-checked:bg-control',
        'data-disabled:cursor-not-allowed data-disabled:bg-subtle',
      )}
    >
      <BaseRadio.Indicator className="size-8 rounded-full bg-white data-unchecked:hidden" />
    </BaseRadio.Root>
  );
}

/** RadioGroup with labelled options (arrow keys move the selection). */
export function RadioGroup<T>({
  options,
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  name,
  orientation = 'vertical',
  className,
  ...aria
}: RadioGroupProps<T>) {
  return (
    <BaseRadioGroup
      {...aria}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(next as T)}
      disabled={disabled}
      required={required}
      name={name}
      className={cn('flex gap-12', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap gap-20', className)}
    >
      {options.map((option) => (
        <label
          key={String(option.value)}
          className={cn('flex items-start gap-10', option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
        >
          <Radio value={option.value} disabled={option.disabled} />
          <span className="flex min-w-0 flex-col gap-2 pt-1">
            <span className={choiceLabel}>{option.label}</span>
            {option.description && <span className={choiceDescription}>{option.description}</span>}
          </span>
        </label>
      ))}
    </BaseRadioGroup>
  );
}
